import React from 'react';
import { useSnackbar } from 'pounceV2';
import { useEditAIInferenceStream } from 'Components/AIExplainLogEvents/graphql/editAiInferenceStream.generated';
import { extractErrorMessage } from 'Helpers/utils';
import { removeAIInferenceStreamFromCache } from 'Components/AIExplainLogEvents/utils/aiInferenceStreamCacheUtils';
import useUrlParams from 'Hooks/useUrlParams';
import { AISummaryStreamParams } from 'Components/AIExplainLogEvents/useFetchGenericAiSummaryV3';
import { useGetAIInferenceStreamMetadata } from 'Components/AIExplainLogEvents/graphql/getAiInferenceStreamMetadata.generated';
import useCopyToClipboardV3 from 'Hooks/useCopyToClipboardV3';
import useFeatureToggle from 'Hooks/useFeatureToggle';

interface UseAIPageActionsProps {
  streamIdForMetadata: string | undefined;
  streamMetadata: ReturnType<typeof useGetAIInferenceStreamMetadata>['data'];
  onResetUI: () => void;
  onClose?: () => void;
  streamsMetadata?: Array<{
    streamId: string;
  }>;
  /**
   * For AIPage: pass hasActiveStream to restrict editing
   * For sidesheets: omit or pass undefined to allow editing when stream exists
   */
  hasActiveStream?: boolean;
}

interface UseAIPageActionsReturn {
  handleNewPrompt: () => void;
  handlePin: () => void;
  handleCopyLink: () => void;
  handleDelete: () => void;
  handleTogglePrivacy: () => void;
  canEdit: boolean;
  isLoading: boolean;
}

/**
 * Hook for managing AI page actions
 */
export const useAIPageActionsV3 = ({
  streamIdForMetadata,
  streamMetadata,
  onResetUI,
  onClose,
  streamsMetadata,
  hasActiveStream,
}: UseAIPageActionsProps): UseAIPageActionsReturn => {
  const { pushSnackbar } = useSnackbar();
  const { updateUrlParams, urlParams } = useUrlParams<AISummaryStreamParams>();
  const copyToClipboard = useCopyToClipboardV3();
  const enablePrivacyControls = useFeatureToggle('ENABLE_AI_PRIVACY_CONTROLS');
  const [editAIInferenceStream, { loading: editAIInferenceStreamLoading }] =
    useEditAIInferenceStream();

  // When privacy controls are disabled, everyone can edit any stream
  // When enabled, use the backend canEdit permission check
  const hasEditPermission =
    !enablePrivacyControls || Boolean(streamMetadata?.aiInferenceStream?.canEdit);

  // AIPage: allow editing if no active stream OR user has permission
  // Sidesheet: allow editing if no streamId (new prompt state) OR user has permission on existing stream
  const canEdit =
    hasActiveStream === undefined
      ? !streamIdForMetadata || hasEditPermission
      : !hasActiveStream || hasEditPermission;

  const handleNewPrompt = React.useCallback(() => {
    updateUrlParams({ aiStream: undefined, parentStreamId: undefined });
    onResetUI();
  }, [updateUrlParams, onResetUI]);

  const handlePin = React.useCallback(() => {
    if (!streamIdForMetadata) {
      return;
    }

    const isCurrentlyPinned = streamMetadata?.aiInferenceStream?.pinned ?? false;

    editAIInferenceStream({
      variables: {
        input: {
          streamId: streamIdForMetadata,
          pinned: !isCurrentlyPinned,
        },
      },
      onCompleted: () => {
        pushSnackbar({
          variant: 'success',
          title: isCurrentlyPinned ? 'Response unpinned' : 'Response pinned',
        });
      },
      onError: error => {
        pushSnackbar({
          variant: 'error',
          title: 'Failed to pin response',
          description: extractErrorMessage(error),
        });
      },
      refetchQueries: ['ListAIInferenceStreamsMetadata'],
    });
  }, [
    streamIdForMetadata,
    streamMetadata?.aiInferenceStream?.pinned,
    editAIInferenceStream,
    pushSnackbar,
  ]);

  const handleCopyLink = React.useCallback(() => {
    if (streamIdForMetadata) {
      const shareableUrl = `${window.location.origin}${window.location.pathname}?aiStream=${streamIdForMetadata}`;
      copyToClipboard(shareableUrl, {
        variant: 'success',
        title: 'Link copied to clipboard',
        duration: 1500,
      });
    }
  }, [streamIdForMetadata, copyToClipboard]);

  const handleTogglePrivacy = React.useCallback(() => {
    if (!streamIdForMetadata) {
      return;
    }

    const currentPrivacy = streamMetadata?.aiInferenceStream?.private ?? true;

    editAIInferenceStream({
      variables: {
        input: {
          streamId: streamIdForMetadata,
          private: !currentPrivacy,
        },
      },
      onCompleted: () => {
        pushSnackbar({
          variant: 'success',
          title: `Chat is now ${!currentPrivacy ? 'private' : 'shared'}`,
        });
      },
      onError: error => {
        pushSnackbar({
          variant: 'error',
          title: 'Failed to update privacy',
          description: extractErrorMessage(error),
        });
      },
      refetchQueries: ['GetAIInferenceStreamMetadata', 'ListAIInferenceStreamsMetadata'],
      awaitRefetchQueries: true,
    });
  }, [
    streamIdForMetadata,
    streamMetadata?.aiInferenceStream?.private,
    editAIInferenceStream,
    pushSnackbar,
  ]);

  const handleDelete = React.useCallback(() => {
    if (!streamIdForMetadata) {
      return;
    }

    editAIInferenceStream({
      variables: { input: { streamId: streamIdForMetadata, deleted: true } },
      onCompleted: () => {
        pushSnackbar({
          variant: 'success',
          title: 'Response deleted',
        });
        // Navigate away from deleted stream
        if (onClose && streamsMetadata) {
          const remainingThreads = streamsMetadata.filter(
            thread => thread.streamId !== streamIdForMetadata,
          );
          const isCurrentStream = urlParams?.aiStream === streamIdForMetadata;

          // If deleting current stream and others exist, navigate to first remaining stream
          if (isCurrentStream && remainingThreads.length) {
            updateUrlParams({ aiStream: remainingThreads[0].streamId });
          } else {
            // Clear stream param, close panel if deleting current stream or last thread
            updateUrlParams({ aiStream: undefined });
            if (isCurrentStream || remainingThreads.length === 0) {
              onClose();
            }
          }
        } else {
          // Otherwise just navigate away from deleted stream
          updateUrlParams({ aiStream: undefined, parentStreamId: undefined });
        }

        onResetUI();
      },
      onError: error => {
        pushSnackbar({
          variant: 'error',
          title: 'Failed to delete response',
          description: extractErrorMessage(error),
        });
      },
      update: cache => {
        removeAIInferenceStreamFromCache(cache, streamIdForMetadata);
      },
      awaitRefetchQueries: true,
    });
  }, [
    streamIdForMetadata,
    editAIInferenceStream,
    pushSnackbar,
    updateUrlParams,
    urlParams?.aiStream,
    onResetUI,
    onClose,
    streamsMetadata,
  ]);

  return {
    handleNewPrompt,
    handlePin,
    handleCopyLink,
    handleDelete,
    handleTogglePrivacy,
    canEdit,
    isLoading: editAIInferenceStreamLoading,
  };
};
